import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

interface FieldProps {
  id: string
  label: string
  error?: string | null
  help?: string
  children: ReactNode
  className?: string
}

/** Label + control + help / error line, for dialogs and admin forms. */
export function Field({ id, label, error, help, children, className }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id} className="text-meta font-medium text-ink">
        {label}
      </Label>
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-label text-status-escalated-fg">
          {error}
        </p>
      ) : help ? (
        <p className="text-label text-ink-muted">{help}</p>
      ) : null}
    </div>
  )
}

/**
 * Native select styled like `<Input>`. Used inside dialogs, where a native
 * control avoids portal/focus-trap issues and works with every keyboard and
 * screen reader.
 */
export const NativeSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-body text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
)
NativeSelect.displayName = 'NativeSelect'
